import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ArrowRight, Compass, Target, Sparkles, LineChart, Layers } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SERVICES, CHALLENGES, CASE_STUDIES, CLIENTS } from "@/data/content";
import { HOME } from "@/constants/testIds";

const ServiceIcon = ({ id, size = 20 }) => {
  const map = {
    "go-to-market-strategy": Compass,
    "market-customer-insights": LineChart,
    "demand-generation-abm": Target,
    "campaign-media-management": Layers,
    "creative-content": Sparkles,
  };
  const Icon = map[id] || Compass;
  return <Icon size={size} className="text-clay-400" />;
};

export default function Home() {
  const { t } = useTranslation();

  const stats = [
    { value: "£340M+", label: t("home.statsPipeline") },
    { value: "18", label: t("home.statsCountries") },
    { value: "94%", label: t("home.statsRetention") },
    { value: "2013", label: t("home.statsFounded") },
  ];

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1523875194681-bedd468c58bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJ1c2luZXNzJTIwc3RyYXRlZ3l8ZW58MHx8fHwxNzg0MTI1NDA3fDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-950 via-ink-950/70 to-ink-950" />
          <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(192,94,76,0.18),transparent_70%)]" />
        </div>

        <div className="container-x pt-24 md:pt-36 pb-28 md:pb-40">
          <Reveal><p className="eyebrow mb-8">{t("home.eyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1 max-w-5xl">
              {t("home.heroTitle1")}<br />
              <span className="italic text-clay-400">{t("home.heroTitleItalic")}</span> {t("home.heroTitle2")}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="body-lg mt-10 max-w-2xl">{t("home.heroDescription")}</p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary" data-testid={HOME.heroCta}>
                {t("home.heroCta")}
                <ArrowUpRight size={16} />
              </Link>
              <Link to="/services" className="btn-ghost" data-testid={HOME.heroSecondary}>
                {t("home.heroSecondary")}
              </Link>
            </div>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8 border-t border-white/10 pt-10">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-4xl md:text-5xl text-bone-100">{s.value}</div>
                  <div className="mt-2 text-xs uppercase tracking-widest2 text-bone-200/50">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLIENT MARQUEE */}
      <section className="relative border-y border-white/5 bg-ink-900/50 py-10 overflow-hidden">
        <div className="flex animate-marquee gap-16 whitespace-nowrap">
          {[...CLIENTS, ...CLIENTS].map((name, i) => (
            <span key={i} className="font-display text-3xl md:text-4xl text-bone-100/25 tracking-wide">
              {name}
            </span>
          ))}
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="relative py-24 md:py-36">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <Reveal><p className="eyebrow mb-6">{t("home.challengesEyebrow")}</p></Reveal>
              <Reveal delay={80}>
                <h2 className="display-h2">{t("home.challengesTitle")}</h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="body-lg mt-8 max-w-md">{t("home.challengesDescription")}</p>
              </Reveal>
            </div>

            <div className="md:col-span-7 md:pl-10">
              <ul className="divide-y divide-white/10 border-y border-white/10">
                {CHALLENGES.map((c, i) => (
                  <Reveal key={c.number} delay={i * 60}>
                    <li className="group grid grid-cols-12 gap-6 py-8 transition-colors hover:bg-white/[0.02]">
                      <div className="col-span-2 md:col-span-1 font-display text-3xl text-clay-400">{c.number}</div>
                      <div className="col-span-10 md:col-span-11">
                        <h3 className="display-h3 mb-2">{t(`challenges.${c.i18nKey}Title`)}</h3>
                        <p className="text-bone-200/70 leading-relaxed max-w-xl">
                          {t(`challenges.${c.i18nKey}Body`)}
                        </p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="relative py-24 md:py-36 bg-ink-900/40 border-y border-white/5">
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("home.methodEyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h2 className="display-h2 max-w-3xl">{t("home.methodTitle")}</h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            <Reveal delay={120}>
              <div className="card-surface p-10 md:p-14 h-full relative overflow-hidden">
                <div className="absolute -right-10 -top-10 font-display text-[16rem] leading-none text-white/[0.03] select-none">01</div>
                <p className="text-xs uppercase tracking-widest2 text-clay-400 mb-6">Shape</p>
                <h3 className="display-h3 mb-6">{t("home.shapeTitle")}</h3>
                <p className="text-bone-200/75 leading-relaxed">{t("home.shapeBody")}</p>
                <Link to="/services" className="link-arrow mt-10">
                  {t("home.shapeCta")}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="card-surface p-10 md:p-14 h-full relative overflow-hidden">
                <div className="absolute -right-10 -top-10 font-display text-[16rem] leading-none text-white/[0.03] select-none">02</div>
                <p className="text-xs uppercase tracking-widest2 text-clay-400 mb-6">Execute</p>
                <h3 className="display-h3 mb-6">{t("home.executeTitle")}</h3>
                <p className="text-bone-200/75 leading-relaxed">{t("home.executeBody")}</p>
                <Link to="/case-studies" className="link-arrow mt-10">
                  {t("home.executeCta")}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-24 md:py-36">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
            <div className="max-w-2xl">
              <Reveal><p className="eyebrow mb-6">{t("home.servicesEyebrow")}</p></Reveal>
              <Reveal delay={80}>
                <h2 className="display-h2">{t("home.servicesTitle")}</h2>
              </Reveal>
            </div>
            <Reveal delay={160}>
              <Link to="/services" className="btn-ghost">
                {t("common.allServices")}
                <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const isExternal = s.external;
              const commonInner = (
                <>
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-clay-400 transition-colors">
                      <ServiceIcon id={s.id} />
                    </div>
                    <span className="font-display text-clay-400/70 text-lg">{s.number}</span>
                  </div>
                  <h3 className="display-h3 mb-3">{t(`servicesData.${s.i18nKey}.title`)}</h3>
                  <p className="text-bone-200/70 leading-relaxed text-sm mb-8 min-h-[3.5rem]">
                    {t(`servicesData.${s.i18nKey}.tagline`)}
                  </p>
                  <ul className="space-y-2 mb-10">
                    {s.capabilities.slice(0, 3).map((cap) => (
                      <li key={cap} className="text-sm text-bone-100/80 flex items-start gap-2">
                        <span className="text-clay-400 mt-1">—</span>
                        {cap}
                      </li>
                    ))}
                    {s.capabilities.length > 3 && (
                      <li className="text-xs text-bone-200/40 pl-4 uppercase tracking-widest2">
                        +{s.capabilities.length - 3} {t("home.moreCapabilities")}
                      </li>
                    )}
                  </ul>
                  <div className="flex items-center gap-2 text-clay-400 text-sm font-semibold uppercase tracking-widest2 group-hover:gap-3 transition-all">
                    {t("common.learnMore")}
                    {isExternal ? <ArrowUpRight size={14} /> : <ArrowRight size={14} />}
                  </div>
                </>
              );

              return (
                <Reveal key={s.id} delay={i * 60}>
                  {isExternal ? (
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${HOME.serviceLearnMorePrefix}-${s.id}`}
                      className="group card-surface p-8 lg:p-10 block h-full"
                    >
                      {commonInner}
                    </a>
                  ) : (
                    <Link
                      to={s.href}
                      state={{ focus: s.id }}
                      data-testid={`${HOME.serviceLearnMorePrefix}-${s.id}`}
                      className="group card-surface p-8 lg:p-10 block h-full"
                    >
                      {commonInner}
                    </Link>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CASE STUDIES PREVIEW */}
      <section className="relative py-24 md:py-36 bg-ink-900/40 border-y border-white/5">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
            <div className="max-w-2xl">
              <Reveal><p className="eyebrow mb-6">{t("home.workEyebrow")}</p></Reveal>
              <Reveal delay={80}>
                <h2 className="display-h2">{t("home.workTitle")}</h2>
              </Reveal>
            </div>
            <Reveal delay={160}>
              <Link to="/case-studies" className="btn-ghost">
                {t("common.allCaseStudies")}
                <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {CASE_STUDIES.slice(0, 3).map((c, i) => (
              <Reveal key={c.slug} delay={i * 80} className={i === 0 ? "md:col-span-7" : "md:col-span-5"}>
                <Link
                  to={`/case-studies/${c.slug}`}
                  data-testid={`home-case-card-${c.slug}`}
                  className="group h-full card-surface overflow-hidden flex flex-col"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={c.image} alt="" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-6">
                      <span className="text-xs uppercase tracking-widest2 text-clay-400">{c.industry}</span>
                    </div>
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-baseline gap-4 mb-6">
                      <span className="font-display text-4xl text-clay-400">{c.metric}</span>
                      <span className="text-xs uppercase tracking-widest2 text-bone-200/50">{c.metricLabel}</span>
                    </div>
                    <h3 className="display-h3 mb-4">{c.title}</h3>
                    <p className="text-sm text-bone-200/70 leading-relaxed mb-8">{c.excerpt}</p>
                    <span className="link-arrow mt-auto text-sm font-semibold uppercase tracking-widest2">
                      {t("common.readMore")}
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="relative py-24 md:py-36">
        <div className="container-x">
          <div className="max-w-4xl">
            <Reveal><p className="eyebrow mb-10">{t("home.testimonialEyebrow")}</p></Reveal>
            <Reveal delay={80}>
              <blockquote className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-bone-100">
                <span className="text-clay-400">"</span>
                {t("home.testimonialQuote")}
                <span className="text-clay-400">"</span>
              </blockquote>
            </Reveal>
            <Reveal delay={160}>
              <div className="mt-10 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-clay-500/20 border border-clay-400/40 flex items-center justify-center font-display text-clay-400">EM</div>
                <div>
                  <div className="text-bone-100 font-semibold">{t("home.testimonialAuthor")}</div>
                  <div className="text-xs uppercase tracking-widest2 text-bone-200/50">{t("home.testimonialRole")}</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-ink-800 to-ink-900 p-10 md:p-20">
              <div className="absolute -right-32 -bottom-32 w-[500px] h-[500px] rounded-full bg-clay-500/10 blur-3xl" />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-8">
                  <p className="eyebrow mb-6">{t("home.ctaEyebrow")}</p>
                  <h2 className="display-h2 max-w-2xl">{t("home.ctaTitle")}</h2>
                  <p className="body-lg mt-6 max-w-xl">{t("home.ctaBody")}</p>
                </div>
                <div className="md:col-span-4 md:text-right">
                  <Link to="/contact" className="btn-primary" data-testid={HOME.finalCta}>
                    {t("common.bookConsultation")}
                    <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
